/**
 * Agent Events System
 * 
 * Provides a simple event bus for the AI agent to trigger UI refreshes
 * when data changes are made through the chatbot.
 */

export type AgentEventType = 
  | 'supplier:added'
  | 'supplier:updated'
  | 'supplier:deleted'
  | 'poam:created'
  | 'poam:updated'
  | 'data:refresh';

export interface AgentEvent {
  type: AgentEventType;
  payload?: Record<string, unknown>;
  timestamp: number;
}

type EventCallback = (event: AgentEvent) => void;

class AgentEventBus {
  private listeners: Map<AgentEventType | '*', Set<EventCallback>> = new Map();

  /**
   * Subscribe to a specific event type or all events with '*'
   */
  subscribe(eventType: AgentEventType | '*', callback: EventCallback): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(eventType)?.delete(callback);
    };
  }

  /**
   * Emit an event to all subscribers
   */
  emit(type: AgentEventType, payload?: Record<string, unknown>): void {
    const event: AgentEvent = {
      type,
      payload,
      timestamp: Date.now(),
    };

    // Notify specific listeners
    this.listeners.get(type)?.forEach(callback => {
      try {
        callback(event);
      } catch (err) {
        console.error('[AgentEventBus] Error in listener', { type, err });
      }
    });

    // Notify wildcard listeners
    this.listeners.get('*')?.forEach(callback => {
      try {
        callback(event);
      } catch (err) {
        console.error(`[AgentEventBus] Error in wildcard listener:`, err);
      }
    });
  }
}

// Singleton instance
export const agentEvents = new AgentEventBus();
